import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Common timezones list (ordered by UTC offset)
const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10)' },
  { value: 'America/Anchorage', label: 'Alaska (UTC-9)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' },
  { value: 'America/Denver', label: 'Mountain Time (UTC-7)' },
  { value: 'America/Chicago', label: 'Central Time (UTC-6)' },
  { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
  { value: 'Europe/London', label: 'London (UTC+0)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10)' },
];

export default function LoginDialog({ open, onOpenChange, defaultToSignup = false }) {
  const [isLogin, setIsLogin] = useState(!defaultToSignup);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  // Detect browser timezone on mount
  useEffect(() => {
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(detectedTimezone);
    } catch (error) {
      console.error('Error detecting timezone:', error);
      setTimezone('UTC');
    }
  }, []);

  // Reset form when dialog opens/closes or mode changes
  useEffect(() => {
    if (open) {
      setIsLogin(!defaultToSignup);
      setUsername('');
      setPassword('');
    }
  }, [open, defaultToSignup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!isLogin && username.length < 5) {
      toast.error('Username must be at least 5 characters');
      return;
    }

    if (!isLogin && !/^[a-zA-Z0-9]+$/.test(username)) {
      toast.error('Username must be alphanumeric only');
      return;
    }

    setLoading(true);

    const result = isLogin 
      ? await login(username, password, false)
      : await register(username, password, timezone);

    setLoading(false);

    if (result.success) {
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created!');
      onOpenChange(false);
      navigate('/app');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
              tabIndex={1}
            />
            {!isLogin && (
              <p className="text-xs text-muted-foreground">
                Alphanumeric only, minimum 5 characters
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              tabIndex={2}
            />
          </div>

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone} disabled={loading}>
                <SelectTrigger id="timezone" tabIndex={3}>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Detected: {timezone}
              </p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full text-white hover:!text-black"
            disabled={loading}
            variant="default"
            style={{ backgroundColor: 'var(--color-primary)' }}
            tabIndex={isLogin ? 3 : 4}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Log In' : 'Sign Up')}
          </Button>
        </form>

        {/* Toggle Login/Signup */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm hover:underline"
            style={{ color: 'var(--color-primary)' }}
            disabled={loading}
            tabIndex={isLogin ? 4 : 5}
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Log in'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
